import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LANGUAGE_STORAGE_KEY = "luna.language";

const translations = {
  "Dashboard": "Dasbor",
  "Sales": "Penjualan",
  "Services": "Layanan",
  "Retail products": "Produk retail",
  "Inventory": "Inventaris",
  "Vendors": "Vendor",
  "Purchase orders": "Pesanan pembelian",
  "Marketing": "Pemasaran",
  "Staff": "Staf",
  "Payroll": "Penggajian",
  "Expenses": "Pengeluaran",
  "AI Insights": "Wawasan AI",
  "Settings": "Pengaturan",
  "Studio workspace": "Ruang kerja studio",
  "CRM integration": "Integrasi CRM",
  "Connect CRM app": "Hubungkan aplikasi CRM",
  "Sync clients, bookings and customer activity when your CRM is ready.": "Sinkronkan klien, booking, dan aktivitas pelanggan saat CRM Anda siap.",
  "Connection settings": "Pengaturan koneksi",
  "Workspace settings": "Pengaturan ruang kerja",
  "Salon operations": "Operasional salon",
  "Salon accounting & operations": "Akuntansi & operasional salon",
  "Data source": "Sumber data",
  "Good morning": "Selamat pagi",
  "Good afternoon": "Selamat siang",
  "Good evening": "Selamat malam",
  "Working late": "Bekerja hingga larut",
  "Here's your studio today.": "Inilah studio Anda hari ini.",
  "A snapshot across bookings, services, retail and profit.": "Ringkasan booking, layanan, retail, dan keuntungan.",
  "Revenue": "Pendapatan",
  "Net of tax & shipping": "Setelah pajak & pengiriman",
  "Avg ticket": "Rata-rata transaksi",
  "Net profit": "Laba bersih",
  "Expense mix": "Komposisi pengeluaran",
  "Revenue over time": "Pendapatan dari waktu ke waktu",
  "Revenue by offering": "Pendapatan berdasarkan penawaran",
  "Top revenue drivers": "Sumber pendapatan teratas",
  "Get started": "Mulai",
  "Make your studio yours": "Sesuaikan studio Anda",
  "Add your services, team, retail products and operating expenses.": "Tambahkan layanan, tim, produk retail, dan biaya operasional.",
  "Add service": "Tambah layanan",
  "Add product": "Tambah produk",
  "Add team member": "Tambah anggota tim",
  "New service": "Layanan baru",
  "Edit service": "Edit layanan",
  "New product": "Produk baru",
  "Edit product": "Edit produk",
  "New team member": "Anggota tim baru",
  "Edit team member": "Edit anggota tim",
  "Record sale": "Catat penjualan",
  "Add expense": "Tambah pengeluaran",
  "New expense": "Pengeluaran baru",
  "Edit expense": "Edit pengeluaran",
  "Save": "Simpan",
  "Cancel": "Batal",
  "Delete": "Hapus",
  "Search": "Cari",
  "Search…": "Cari…",
  "No match": "Tidak ditemukan",
  "All sources": "Semua sumber",
  "External API": "API eksternal",
  "In studio": "Di studio",
  "English": "Inggris",
  "Indonesian": "Indonesia",
  "Display currency": "Mata uang tampilan",
  "Indonesian Rupiah": "Rupiah Indonesia",
  "Rates are approximate. Values stored in USD.": "Kurs bersifat perkiraan. Nilai disimpan dalam USD.",
  "Total expenses": "Total pengeluaran",
  "Total revenue": "Total pendapatan",
  "Monthly revenue": "Pendapatan bulanan",
  "Monthly salary": "Gaji bulanan",
  "Monthly payroll": "Penggajian bulanan",
  "Description": "Deskripsi",
  "Name": "Nama",
  "Category": "Kategori",
  "Price": "Harga",
  "Cost": "Biaya",
  "Duration": "Durasi",
  "Active": "Aktif",
  "Inactive": "Tidak aktif",
  "Staff assignments": "Penugasan staf",
  "Owner": "Pemilik",
  "All statuses": "Semua status",
  "All categories": "Semua kategori",
  "No services yet": "Belum ada layanan",
  "No products yet": "Belum ada produk",
  "No staff yet": "Belum ada staf",
  "No expenses yet": "Belum ada pengeluaran",
};

function replaceTextContent(root, language) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let node;
  while ((node = walker.nextNode())) nodes.push(node);

  nodes.forEach((textNode) => {
    if (!textNode.__lunaEnglishText) textNode.__lunaEnglishText = textNode.nodeValue;
    const source = textNode.__lunaEnglishText;
    const trimmed = source.trim();
    if (!trimmed) return;
    const translated = language === "id" ? translations[trimmed] : trimmed;
    const target = translated && translated !== trimmed ? source.replace(trimmed, translated) : source;
    if (textNode.nodeValue !== target) textNode.nodeValue = target;
  });

  root.querySelectorAll("input[placeholder], textarea[placeholder], [title], [aria-label]").forEach((element) => {
    if (!element.__lunaLanguageAttributes) element.__lunaLanguageAttributes = {};
    ["placeholder", "title", "aria-label"].forEach((attribute) => {
      if (!element.hasAttribute(attribute)) return;
      const current = element.getAttribute(attribute);
      if (!element.__lunaLanguageAttributes[attribute]) element.__lunaLanguageAttributes[attribute] = current;
      const source = element.__lunaLanguageAttributes[attribute];
      const target = language === "id" ? (translations[source] || source) : source;
      if (element.getAttribute(attribute) !== target) element.setAttribute(attribute, target);
    });
  });
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => localStorage.getItem(LANGUAGE_STORAGE_KEY) || "en");

  const setLanguage = (nextLanguage) => {
    const normalized = nextLanguage === "id" ? "id" : "en";
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    setLanguageState(normalized);
  };

  useEffect(() => {
    document.documentElement.lang = language === "id" ? "id" : "en";
    let applying = false;
    const apply = () => {
      if (applying) return;
      applying = true;
      replaceTextContent(document.body, language);
      applying = false;
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t: (value) => language === "id" ? (translations[value] || value) : value }), [language]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLanguage = () => useContext(LanguageContext);